"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "next-themes";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { Suspense, useMemo, useEffect } from "react";
import { Toaster as SonnerToaster } from "sonner";
import { ChatSupport } from "@/components/chat-support";
import { ReferralHandler } from "@/components/referral-handler";
import { Toaster } from "@/lib/components/toaster";
import { toast } from "@/lib/components/use-toast";
import { AppConfigProvider } from "@/lib/config";
import type { AppConfig } from "@/lib/config-server";
import type { ReactNode } from "react";
interface ProvidersProps {
  children: ReactNode;
  config: AppConfig;
}
function extractErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;
    if (err.error && typeof err.error === "object") {
      const nestedError = err.error as Record<string, unknown>;
      if (typeof nestedError.message === "string") {
        return nestedError.message;
      }
    }
    if (typeof err.message === "string") {
      return err.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unknown error occurred.";
}
export function Providers(t0) {
  const $ = _c(19);
  const {
    children,
    config
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new QueryClient({
      defaultOptions: {
        queries: {
          refetchOnWindowFocus: false,
          staleTime: 300000,
          retry: false
        },
        mutations: {
          onError: _temp
        }
      }
    });
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  let t2;
  let t3;
  if ($[1] !== config.posthogHost || $[2] !== config.posthogKey) {
    t2 = () => {
      if (!config.posthogKey) {
        return;
      }
      const key = config.posthogKey;
      const host = config.posthogHost;
      const init = () => {
        posthog.init(key, {
          api_host: host,
          capture_pageview: "history_change",
          autocapture: true
        });
      };
      if (typeof requestIdleCallback !== "undefined") {
        const id = requestIdleCallback(init);
        return () => cancelIdleCallback(id);
      }
      const timer = setTimeout(init, 1000);
      return () => clearTimeout(timer);
    };
    t3 = [config.posthogKey, config.posthogHost];
    $[1] = config.posthogHost;
    $[2] = config.posthogKey;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== children) {
    t4 = <PostHogProvider client={posthog}>{children}</PostHogProvider>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = process.env.NODE_ENV === "development" && <ReactQueryDevtools buttonPosition="bottom-left" />;
    t6 = <ChatSupport />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t4) {
    t7 = <QueryClientProvider client={queryClient}>{t4}{t5}{t6}</QueryClientProvider>;
    $[9] = t4;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <Toaster />;
    t9 = <SonnerToaster richColors={true} position="bottom-right" />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Suspense><ReferralHandler /></Suspense>;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== t7) {
    t11 = <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} storageKey="theme">{t7}{t8}{t9}{t10}</ThemeProvider>;
    $[14] = t7;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== config || $[17] !== t11) {
    t12 = <AppConfigProvider config={config}>{t11}</AppConfigProvider>;
    $[16] = config;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  return t12;
}
function _temp(error) {
  const errorMessage = extractErrorMessage(error);
  toast({
    title: errorMessage,
    variant: "destructive"
  });
}